// Experience and training, in the spirit of Might and Magic III: experience
// alone never levels a character up. They must find a trainer and pay for it,
// one level at a time.

import { statBonus } from './combat/rules';

// Hit points gained per level, by class
const HIT_DICE = {
    Knight: 10,
    Barbarian: 12,
    Ranger: 8,
    Robber: 6,
    Cleric: 6,
    Sorcerer: 4,
};

// Spell points come from Personality for clerical casters, Intellect for arcane
const CASTING_STAT = {
    Cleric: 'personality',
    Ranger: 'personality',
    Sorcerer: 'intellect',
};

// Total experience needed to reach a level: 1000 for level 2, then a steadily
// growing step (2500, 4500, 7000, 10000, ...)
export function xpForLevel(level) {
    if (level <= 1) return 0;
    const steps = level - 1;
    return 1000 * steps + 250 * steps * (steps - 1);
}

export function xpToNext(character) {
    return Math.max(0, xpForLevel(character.level + 1) - character.xp);
}

// How far along this level the character is, as 0..1
export function levelProgress(character) {
    const floor = xpForLevel(character.level);
    const ceiling = xpForLevel(character.level + 1);
    if (character.xp >= ceiling) return 1;
    return Math.max(0, (character.xp - floor) / (ceiling - floor));
}

export function trainingCost(character) {
    return character.level * 100;
}

export function hasExperienceToTrain(character) {
    return character.xp >= xpForLevel(character.level + 1);
}

// Why this character can't train right now, or null if they can
export function trainingBlocker(character, gold) {
    if (character.condition === 'dead') return `${character.name} is beyond training.`;
    if (!hasExperienceToTrain(character)) {
        return `${character.name} needs ${xpToNext(character)} more experience.`;
    }
    if (gold < trainingCost(character)) {
        return `Training ${character.name} costs ${trainingCost(character)} gold.`;
    }
    return null;
}

// One level up: more hit points, more spell points for casters, and the
// character is healed by what they gained.
export function levelUp(character) {
    const hpGain = Math.max(1, (HIT_DICE[character.className] || 6) + statBonus(character.stats.endurance));
    const castingStat = CASTING_STAT[character.className];
    const spGain = character.maxSp > 0 && castingStat
        ? Math.max(1, 2 + statBonus(character.stats[castingStat]))
        : 0;

    return {
        ...character,
        level: character.level + 1,
        maxHp: character.maxHp + hpGain,
        hp: character.condition === 'dead' ? character.hp : character.hp + hpGain,
        maxSp: character.maxSp + spGain,
        sp: character.sp + spGain,
        hpGain,
        spGain,
    };
}
